import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiService, updateChatRoomUserId } from '../../services/api';
import { envConfig } from '../../config/env';
import { cleanEmailContent, needsCleaning } from '../../utils/emailContentCleaner';
import { useSSE } from '../../hooks/useWebSocket';
import { getApiBaseUrl } from '../../config/env';
import { ChatRoom } from '../../types';
import { 
  ChevronUp, 
  ChevronDown, 
  Send, 
  RefreshCw, 
  AlertCircle, 
  Check,
  Type,
  Code,
  Tag,
  Edit,
  X,
  Plus,
  Languages,
  Paperclip,
  Download,
  FileText,
  Image,
  Film,
  Music,
  Archive,
  File,
  Eye,
  EyeOff,
  Copy,
  Wand2,
  Globe,
  User,
  CheckCircle
} from 'lucide-react';

// React Quill导入
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/email-content.css';
import AttachmentPreview from '../AttachmentPreview';

// 自定义工具栏配置
const toolbarOptions = [
  [{ 'header': [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ 'color': [] }, { 'background': [] }],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  ['link'],
  ['clean']
];

// React Quill模块配置
const quillModules = {
  toolbar: toolbarOptions
};

interface ChatMessage {
  id: number;
  message_type: 'user_message' | 'reply_message';
  sender_email: string;
  sender_name?: string;
  recipient_email: string;
  subject: string;
  content: string;
  html_content?: string;
  cleaned_content?: string;
  sent_time?: string;
  ai_analysis_status: 'none' | 'pending' | 'analyzing' | 'completed' | 'failed';
  sending_status?: 'sending' | 'sent' | 'failed';
  has_attachments?: boolean;
  attachments?: Array<{
    filename: string;
    size: number;
    content_type: string;
    s3_url?: string;
    s3_key?: string;
  }>;
  attachment_count?: number;
  ai_analysis?: {
    chinese_translation?: string;
    chinese_summary?: string;
    tags?: string[];
    suggested_reply?: string;
    reply_chinese_translation?: string;
    category?: string;
    sentiment?: string;
    priority_score?: number;
    confidence_scores?: {
      translation?: number;
      summary?: number;
      tags?: number;
      reply?: number;
      category?: number;
      sentiment?: number;
      priority?: number;
    };
  };
  is_read: boolean;
  is_important: boolean;
}



const ChatRoomView: React.FC = () => {
  const { userEmail } = useParams<{ userEmail: string }>();
  const [chatRoom, setChatRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzingMessages, setAnalyzingMessages] = useState<Set<number>>(new Set());
  const [showingOriginal, setShowingOriginal] = useState<Set<number>>(new Set());
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());
  const [unexpandedAIAnalysis, setUnexpandedAIAnalysis] = useState<Set<number>>(new Set());
  const [isComposerExpanded, setIsComposerExpanded] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyHtmlContent, setReplyHtmlContent] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [isRichText, setIsRichText] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editingTags, setEditingTags] = useState('');
  const [updatingTags, setUpdatingTags] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 添加用户ID编辑相关状态
  const [isEditingUserId, setIsEditingUserId] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState(false);

  // 添加状态来跟踪每个消息的回复建议编辑内容
  const [editingSuggestedReplies, setEditingSuggestedReplies] = useState<Record<string, string>>({});
  const [sendingDirectReply, setSendingDirectReply] = useState<Set<number>>(new Set());

  // 添加状态来跟踪中文翻译编辑
  const [translating, setTranslating] = useState<Set<number>>(new Set());
  
  // 添加状态来跟踪附件加载
  const [loadingAttachments, setLoadingAttachments] = useState<Set<string>>(new Set());

  // 添加状态来跟踪自动标记已读
  const [markingAsRead, setMarkingAsRead] = useState(false);

  // 新增：附件预览相关状态
  const [previewAttachments, setPreviewAttachments] = useState<any[]>([]);
  const [previewCurrentIndex, setPreviewCurrentIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // 控制自动滚动的标志位
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  // 附件相关工具函数
  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return Image;
    if (contentType.startsWith('video/')) return Film;
    if (contentType.startsWith('audio/')) return Music;
    if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('text')) return FileText;
    if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('archive')) return Archive;
    return File;
  };

  const getFileIconColor = (contentType: string) => {
    if (contentType.startsWith('image/')) return 'text-green-600';
    if (contentType.startsWith('video/')) return 'text-purple-600';
    if (contentType.startsWith('audio/')) return 'text-blue-600';
    if (contentType.includes('pdf')) return 'text-red-600';
    if (contentType.includes('document')) return 'text-blue-600';
    if (contentType.includes('text')) return 'text-gray-600';
    if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('archive')) return 'text-orange-600';
    return 'text-gray-600';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isPreviewable = (contentType: string) => {
    return contentType.startsWith('image/') || 
           contentType.startsWith('video/') || 
           contentType.startsWith('audio/') ||
           contentType.includes('pdf') ||
           contentType.includes('text');
  };

  const handleAttachmentClick = (attachment: any, allAttachments: any[] = []) => {
    if (!attachment.s3_url || !attachment.uploaded_to_s3) {
      const confirmMessage = `附件 "${attachment.filename}" 暂时无法下载。\n\n这可能是因为：\n- 附件正在处理中\n- 需要重新同步附件数据\n\n是否尝试重新处理该邮件的附件？`;
      
      if (window.confirm(confirmMessage)) {
        // 调用重新处理附件的API
        handleReprocessAttachments();
      }
      return;
    }

    // 检查是否支持预览
    if (isPreviewable(attachment.content_type)) {
      // 打开全屏预览
      const currentIndex = allAttachments.findIndex(att => 
        att.filename === attachment.filename && att.size === attachment.size
      );
      setPreviewAttachments(allAttachments);
      setPreviewCurrentIndex(Math.max(0, currentIndex));
      setIsPreviewOpen(true);
    } else {
      // 直接下载
      const link = document.createElement('a');
      link.href = attachment.s3_url;
      link.download = attachment.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 添加重新处理附件的函数
  const handleReprocessAttachments = async () => {
    try {
      // 找到当前显示的邮件ID
      const currentMessage = messages.find(msg => msg.has_attachments && msg.attachments && msg.attachments.length > 0);
      if (!currentMessage) {
        window.alert('未找到需要处理的邮件');
        return;
      }

      console.log(`🔄 开始重新处理邮件 ${currentMessage.id} 的附件`);
      
      const response = await apiService.post(`/emails/${currentMessage.id}/reprocess-attachments?download_content=true`);
      
      if (response.data.success) {
        window.alert(`附件重新处理成功！共处理了 ${response.data.data.attachment_count} 个附件。请刷新页面查看最新状态。`);
        
        // 刷新消息列表
        await fetchMessages();
      } else {
        window.alert('附件重新处理失败，请稍后重试');
      }
    } catch (error) {
      console.error('重新处理附件失败:', error);
      window.alert('重新处理附件失败，请检查网络连接或联系技术支持');
    }
  };

  // 新增：用于记录每条消息内容是否需要折叠
  const [needsCollapseMap, setNeedsCollapseMap] = useState<Record<number, boolean>>({});
  // 新增：每条消息内容的ref
  const messageContentRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // 检查内容是否溢出
  useEffect(() => {
    const newNeedsCollapseMap: Record<number, boolean> = {};
    messages.forEach((msg) => {
      const ref = messageContentRefs.current[msg.id];
      if (ref) {
        // 临时移除maxHeight限制来获取真实高度
        const originalMaxHeight = ref.style.maxHeight;
        ref.style.maxHeight = 'none';
        
        // 200px为最大高度，和style保持一致
        if (ref.scrollHeight > 200) {
          newNeedsCollapseMap[msg.id] = true;
        } else {
          newNeedsCollapseMap[msg.id] = false;
        }
        
        // 恢复原始样式
        ref.style.maxHeight = originalMaxHeight;
      }
    });
    setNeedsCollapseMap(newNeedsCollapseMap);
    // eslint-disable-next-line
  }, [messages]);

  // 开始编辑标签
  const handleStartEditTags = () => {
    setIsEditingTags(true);
    setEditingTags(chatRoom?.tags.join(', ') || '');
  };

  // 取消编辑标签
  const handleCancelEditTags = () => {
    setIsEditingTags(false);
    setEditingTags('');
  };

  // 保存标签
  const handleSaveTags = async () => {
    if (!userEmail || !chatRoom) return;
    
    try {
      setUpdatingTags(true);
      
      // 解析标签字符串
      const newTags = editingTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      // 这里需要调用更新聊天室标签的API
      // 由于标签优先从发件人规则获取，我们需要更新发件人规则
      const response = await apiService.get(`/sender-rules?search=${encodeURIComponent(userEmail)}`);
      const rules = response.data.rules;
      
      if (rules && rules.length > 0) {
        const rule = rules[0];
        await apiService.put(`/sender-rules/${rule.id}`, {
          tags: newTags.join(', ')
        });
      }
      
      // 更新本地状态
      setChatRoom({ ...chatRoom, tags: newTags });
      setIsEditingTags(false);
      setEditingTags('');
    } catch (err) {
      console.error('更新标签失败:', err);
      window.alert('更新标签失败，请重试');
    } finally {
      setUpdatingTags(false);
    }
  };

  // 添加用户ID编辑相关处理函数
  const handleStartEditUserId = () => {
    setIsEditingUserId(true);
    setEditingUserId(chatRoom?.user_id || '');
  };

  const handleCancelEditUserId = () => {
    setIsEditingUserId(false);
    setEditingUserId('');
  };

  const handleSaveUserId = async () => {
    if (!userEmail) return;
    
    try {
      setUpdatingUserId(true);
      await updateChatRoomUserId(userEmail, editingUserId);
      setIsEditingUserId(false);
      setEditingUserId('');
      
      // 更新本地状态
      if (chatRoom) {
        setChatRoom({ ...chatRoom, user_id: editingUserId });
      }
      
      console.log('✅ 用户ID更新成功');
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新用户ID失败');
    } finally {
      setUpdatingUserId(false);
    }
  };

  // SSE连接，监听新邮件
  const { connectionStatus } = useSSE({
    url: `${envConfig.API_BASE_URL}/sse/chat-room`,
    onMessage: (message: any) => {
      if (message.type === 'new_email' || message.type === 'new_sent_email') {
        const emailData = message.data;
        
        // 检查邮件是否与当前聊天室相关
        const isRelatedToCurrentRoom = (
          (message.type === 'new_email' && emailData.sender_email === userEmail) ||
          (message.type === 'new_sent_email' && emailData.recipient_email === userEmail)
        );
        
        if (isRelatedToCurrentRoom) {
          console.log(`📨 收到与当前聊天室相关的新邮件: ${emailData.subject}`);
          
          // 确定AI分析状态
          let aiAnalysisStatus: 'none' | 'pending' | 'analyzing' | 'completed' | 'failed' = 'none';
          if (emailData.ai_analysis_status) {
            aiAnalysisStatus = emailData.ai_analysis_status;
          } else if (message.type === 'new_email') {
            // 接收的邮件默认触发AI分析
            aiAnalysisStatus = 'pending';
          }
          
          // 解析附件信息
          let attachments = [];
          let attachmentCount = 0;
          try {
            if (emailData.attachments_info && Array.isArray(emailData.attachments_info)) {
              attachments = emailData.attachments_info;
              attachmentCount = attachments.length;
            }
          } catch (e) {
            console.error('解析附件信息失败:', e);
          }

          // 创建新消息对象
          const newMessage: ChatMessage = {
            id: emailData.id || Date.now(),
            message_type: message.type === 'new_email' ? 'user_message' : 'reply_message',
            subject: emailData.subject,
            sender_email: emailData.sender_email,
            sender_name: emailData.sender_name || '',
            recipient_email: emailData.recipient_email,
            content: emailData.content || emailData.content_text || '',
            html_content: emailData.html_content || emailData.content_html || '',
            cleaned_content: emailData.cleaned_content,
            ai_analysis: undefined,
            ai_analysis_status: aiAnalysisStatus,
            sent_time: emailData.sent_at || emailData.received_at || emailData.sent_date || emailData.received_date || new Date().toISOString(),
            is_read: message.type === 'new_sent_email', // 发送的邮件默认为已读
            is_important: false,
            has_attachments: emailData.has_attachments || attachmentCount > 0,
            attachments: attachments,
            attachment_count: attachmentCount,
            sending_status: message.type === 'new_sent_email' ? 'sent' : undefined
          };
          
          // 检查是否已存在该消息（避免重复）
          setMessages(prevMessages => {
            const exists = prevMessages.some(msg => msg.id === newMessage.id);
            if (exists) {
              console.log('消息已存在，跳过添加');
              return prevMessages;
            }
            
            console.log('添加新消息到列表，AI分析状态:', aiAnalysisStatus);
            // 将新消息添加到数组中，然后按时间排序
            const updatedMessages = [...prevMessages, newMessage];
            return updatedMessages.sort((a: ChatMessage, b: ChatMessage) => {
              const timeA = new Date(a.sent_time || '').getTime();
              const timeB = new Date(b.sent_time || '').getTime();
              return timeA - timeB;
            });
          });
          
        } else {
          console.log(`❌ 新邮件与当前聊天室用户无关 (${userEmail})`);
        }
      } else if (message.type === 'email_attachments_processed') {
        // 邮件附件处理完成，更新对应消息的附件信息
        const attachmentData = message.data;
        console.log('📎 聊天室收到附件处理完成事件:', attachmentData);
        
        if (attachmentData?.id) {
          setMessages(prevMessages => 
            prevMessages.map(msg => {
              // 通过邮件ID匹配消息
              if (msg.id === attachmentData.id) {
                // 解析附件信息
                let attachments = [];
                try {
                  attachments = attachmentData.attachments_info || [];
                } catch (e) {
                  console.error('解析附件信息失败:', e);
                }
                
                return {
                  ...msg,
                  has_attachments: attachmentData.has_attachments,
                  attachment_count: attachmentData.attachment_count,
                  attachments: attachments
                };
              }
              return msg;
            })
          );
          console.log('✅ 聊天室消息附件信息已更新:', attachmentData.message_id, '邮件ID:', attachmentData.id, '附件数量:', attachmentData.attachment_count);
        }
      } else if (message.type === 'ai_analysis_completed') {
        // AI分析完成事件
        const analysisData = message.data;
        console.log('🧠 聊天室收到AI分析完成事件:', analysisData);
        
        if (analysisData?.id) {
          setMessages(prevMessages => 
            prevMessages.map(msg => {
              if (msg.id === analysisData.id) {
                console.log('✅ 更新消息AI分析状态为completed:', analysisData.id);
                
                // 移除分析中状态
                setAnalyzingMessages(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(analysisData.id);
                  return newSet;
                });
                
                return {
                  ...msg,
                  ai_analysis_status: 'completed',
                  ai_analysis: analysisData.analysis_result || {}
                };
              }
              return msg;
            })
          );
        }
      } else if (message.type === 'ai_analysis_failed') {
        // AI分析失败事件
        const analysisData = message.data;
        console.log('❌ 聊天室收到AI分析失败事件:', analysisData);
        
        if (analysisData?.id) {
          setMessages(prevMessages => 
            prevMessages.map(msg => {
              if (msg.id === analysisData.id) {
                console.log('❌ 更新消息AI分析状态为failed:', analysisData.id);
                
                // 移除分析中状态
                setAnalyzingMessages(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(analysisData.id);
                  return newSet;
                });
                
                return {
                  ...msg,
                  ai_analysis_status: 'failed',
                  ai_analysis: analysisData.analysis_result || {}
                };
              }
              return msg;
            })
          );
        }
      } else if (message.type === 'email_sent') {
        // 邮件发送成功事件 - 更新临时消息状态
        const emailData = message.data;
        console.log('✅ 聊天室收到邮件发送成功事件:', emailData);
        
        // 查找并更新对应的临时消息
        if (emailData?.task_id) {
          setMessages(prevMessages => 
            prevMessages.map(msg => {
              // 通过task_id匹配临时消息
              if (msg.id === emailData.task_id) {
                console.log('✅ 更新临时消息状态为已发送:', emailData.task_id);
                return {
                  ...msg,
                  id: emailData.id || msg.id, // 使用真实的邮件ID（如果有）
                  sending_status: 'sent'
                };
              }
              return msg;
            })
          );
        }
      } else if (message.type === 'new_sent_email') {
        // 新发送邮件事件 - 这是IMAP同步后的真实邮件数据
        const emailData = message.data;
        console.log('✅ 聊天室收到新发送邮件事件:', emailData);
        
        // 检查是否与当前聊天室相关
        if (emailData.recipient_email === userEmail) {
          // 查找是否有对应的临时消息需要替换
          setMessages(prevMessages => {
            // 查找可能的临时消息（通过主题和时间匹配）
            const tempMessageIndex = prevMessages.findIndex(msg => 
              msg.message_type === 'reply_message' && 
              msg.sending_status === 'sent' &&
              msg.subject === emailData.subject &&
              Math.abs(new Date(msg.sent_time || '').getTime() - new Date(emailData.sent_at || emailData.received_at).getTime()) < 60000 // 1分钟内
            );
            
            if (tempMessageIndex !== -1) {
              // 替换临时消息
              console.log('✅ 替换临时消息为真实邮件数据:', emailData.id);
              const newMessages = [...prevMessages];
              newMessages[tempMessageIndex] = {
                id: emailData.id,
                message_type: 'reply_message',
                sender_email: emailData.sender_email,
                sender_name: emailData.sender_name || '',
                recipient_email: emailData.recipient_email,
                subject: emailData.subject,
                content: emailData.content,
                html_content: emailData.html_content,
                sent_time: emailData.sent_at || emailData.received_at,
                ai_analysis_status: 'none',
                sending_status: 'sent',
                is_read: true,
                is_important: false,
                has_attachments: emailData.has_attachments,
                attachments: emailData.attachments_info,
                attachment_count: emailData.attachment_count
              };
              return newMessages;
            } else {
              // 没有找到临时消息，添加新消息
              console.log('✅ 添加新发送邮件到聊天室:', emailData.id);
              const newMessage: ChatMessage = {
                id: emailData.id,
                message_type: 'reply_message',
                sender_email: emailData.sender_email,
                sender_name: emailData.sender_name || '',
                recipient_email: emailData.recipient_email,
                subject: emailData.subject,
                content: emailData.content,
                html_content: emailData.html_content,
                sent_time: emailData.sent_at || emailData.received_at,
                ai_analysis_status: 'none',
                sending_status: 'sent',
                is_read: true,
                is_important: false,
                has_attachments: emailData.has_attachments,
                attachments: emailData.attachments_info,
                attachment_count: emailData.attachment_count
              };
              
              // 按时间排序插入
              const newMessages = [...prevMessages, newMessage];
              return newMessages.sort((a, b) => {
                const timeA = new Date(a.sent_time || '').getTime();
                const timeB = new Date(b.sent_time || '').getTime();
                return timeA - timeB;
              });
            }
          });
        }
      }
    },
    onOpen: () => {
      console.log('聊天室SSE连接已建立');
    },
    onClose: () => {
      console.log('聊天室SSE连接已断开');
    },
    onError: (error: any) => {
      console.error('聊天室SSE连接错误:', error);
    }
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (shouldAutoScroll) {
      scrollToBottom();
      setShouldAutoScroll(false); // 滚动后重置标志位
    }
  }, [messages, shouldAutoScroll]);

  const fetchChatRoom = useCallback(async () => {
    if (!userEmail) return;
    
    try {
      setError(null); // 清除之前的错误状态
      console.log('🔍 开始获取聊天室信息:', userEmail);
      const response = await apiService.get(`/sender-rules/chats/${encodeURIComponent(userEmail)}`);
      console.log('✅ 聊天室信息获取成功:', response.data);
      // 直接使用后端返回的数据
      setChatRoom(response.data);
      console.log('✅ 聊天室状态已更新');
    } catch (err) {
      console.error('❌ 获取聊天室信息失败:', err);
      console.error('❌ 错误详情:', {
        message: (err as any).message,
        status: (err as any).response?.status,
        data: (err as any).response?.data,
        config: (err as any).config
      });
      setError('获取聊天室信息失败');
    }
  }, [userEmail]);

  const fetchMessages = useCallback(async () => {
    if (!userEmail) return;
    
    try {
      setError(null); // 清除之前的错误状态
      setLoading(true);
      const response = await apiService.get(`/sender-rules/chats/${encodeURIComponent(userEmail)}/messages`);
      const messagesData = response.data;
      
      // 按时间排序，确保旧的消息在上面，新的在下面
      const sortedMessages = messagesData.sort((a: ChatMessage, b: ChatMessage) => {
        const timeA = new Date(a.sent_time || '').getTime();
        const timeB = new Date(b.sent_time || '').getTime();
        return timeA - timeB;
      });
      
      setMessages(sortedMessages);
      setShouldAutoScroll(true); // 初次加载消息时自动滚动到底部
      
      // 找到最新的用户消息并自动展开
      if (messagesData && messagesData.length > 0) {
        // 从后往前找最新的用户消息
        for (let i = messagesData.length - 1; i >= 0; i--) {
          const message = messagesData[i];
          if (message.message_type === 'user_message') {
            // 自动展开最新的用户消息
            setExpandedMessages(prev => new Set(prev).add(message.id));
            break; // 只展开最新的一条用户消息
          }
        }
      }
    } catch (err) {
      console.error('获取消息失败:', err);
      setError('获取消息失败');
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
          if (userEmail) {
        fetchChatRoom();
        fetchMessages();
      }
  }, [userEmail, fetchChatRoom, fetchMessages]);

  // 自动标记用户邮件为已读
  const markUserEmailsAsRead = useCallback(async () => {
    if (!userEmail || markingAsRead) return;
    
    try {
      setMarkingAsRead(true);
      console.log(`🔄 开始标记用户邮件为已读: ${userEmail}`);
      
      const response = await apiService.put(`/sender-rules/${encodeURIComponent(userEmail)}/mark-read`);
      
      if (response.data.updated_count > 0) {
        console.log(`✅ 成功标记 ${response.data.updated_count} 封邮件为已读`);
        
        // 更新本地消息状态
        setMessages(prevMessages => 
          prevMessages.map(message => 
            message.message_type === 'user_message' && !message.is_read
              ? { ...message, is_read: true }
              : message
          )
        );
      } else {
        console.log(`ℹ️ 没有需要标记为已读的邮件`);
      }
    } catch (error) {
      console.error('❌ 标记邮件为已读失败:', error);
      // 不显示错误提示，避免干扰用户体验
    } finally {
      setMarkingAsRead(false);
    }
  }, [userEmail, markingAsRead]);

  // 在聊天室详情打开时自动标记用户邮件为已读
  useEffect(() => {
    if (userEmail && messages.length > 0) {
      // 检查是否有未读的用户邮件
      const hasUnreadUserMessages = messages.some(
        message => message.message_type === 'user_message' && !message.is_read
      );
      
      if (hasUnreadUserMessages) {
        // 延迟1秒后自动标记为已读，给用户时间看到未读状态
        const timer = setTimeout(() => {
          markUserEmailsAsRead();
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [userEmail, messages, markUserEmailsAsRead]);

  const handleAnalyzeMessage = async (messageId: number) => {
    setAnalyzingMessages(prev => new Set(prev).add(messageId));
    
    // 立即更新消息状态为analyzing
    setMessages(prevMessages => 
      prevMessages.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            ai_analysis_status: 'analyzing'
          };
        }
        return msg;
      })
    );
    
    try {
      const response = await apiService.post(`/ai/trigger-assistant/1`, {
        email_id: messageId
      });
      // 不再需要手动刷新，SSE会自动更新结果
              console.log(`已触发AI分析: ${messageId}，等待SSE更新结果...`);
        
        // 立即清除分析状态，因为API调用已完成
        setAnalyzingMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
          } catch (err) {
        console.error('AI分析失败:', err);
      // 如果触发失败，移除分析状态并设置为failed
      setAnalyzingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
      
      setMessages(prevMessages => 
        prevMessages.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              ai_analysis_status: 'failed'
            };
          }
          return msg;
        })
      );
    }
  };

  const getDisplayContent = (message: ChatMessage) => {
    // const isShowingOriginal = showingOriginal.has(message.id);
    
    // if (isShowingOriginal) {
    //   return message.content;
    // }
    
    // if (message.cleaned_content && message.cleaned_content.trim()) {
    //   return message.cleaned_content;
    // }
    
    // if (message.content && message.content.trim()) {
    //   const { cleanedContent, hasChanges } = cleanEmailContent(message.content);
    //   return hasChanges ? cleanedContent : message.content;
    // }
    
    return message.content;
  };

  const isHtmlContent = (content: string) => {
    if (!content) return false;
    // 检查是否包含HTML标签
    const htmlRegex = /<[^>]+>/;
    return htmlRegex.test(content);
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '未知时间';
    
    // 处理服务器返回的时间格式
    // 如果时间字符串没有时区信息，假设它是UTC时间
    let date: Date;
    if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      // 格式如 "2025-07-10T06:31:41" - 假设为UTC时间
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    
    // 使用Intl.DateTimeFormat进行正确的时区转换
    const nowFormatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
    
    const now = new Date();
    const todayStr = nowFormatter.format(now);
    const messageDateStr = nowFormatter.format(date);
    
    if (todayStr === messageDateStr) {
      // 今天的消息只显示时间
      return new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date);
    } else {
      // 其他日期显示月日和时间
      return new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date);
    }
  };

  const handleSendReply = async () => {
    if ((!replyContent.trim() && !replyHtmlContent.trim()) || !userEmail) return;
    
    // 防止重复提交
    if (sendingReply) {
      console.log('⚠️ 正在发送中，忽略重复点击');
      return;
    }
    
    try {
      setSendingReply(true);
      const lastMessage = messages[messages.length - 1];
      const subject = replySubject || (lastMessage?.subject.startsWith('Re:') ? lastMessage.subject : `Re: ${lastMessage?.subject || '回复'}`);
      
      // 查找用户邮件（message_type为user_message）来确定原始收件人
      const userMessage = messages.find(msg => msg.message_type === 'user_message');
      const replyToEmail = userMessage?.recipient_email || 'service@digen.ai'; // 使用原始邮件的收件人作为回复的发件人
      
      // 获取所有邮箱账号来查找对应的account_id
      const accountsResponse = await apiService.get('/accounts');
      const accounts = accountsResponse.data.accounts || [];
      const targetAccount = accounts.find((acc: any) => acc.email === replyToEmail);
      const accountId = targetAccount?.id || 6; // 如果找不到对应账号，默认使用ID 6
      
      // 发送邮件
      const response = await apiService.post('/emails/send', {
        from: replyToEmail,
        to: userEmail,
        subject: subject,
        content: isRichText ? replyHtmlContent : replyContent,
        html_content: isRichText ? replyHtmlContent : undefined,
        account_id: accountId
      });
      
      // 获取任务ID作为临时消息ID
      const taskId = response.data.task_id;
      
      // 立即创建一个临时的回复消息显示在页面上，状态为"发送中"
      const tempReplyMessage: ChatMessage = {
        id: taskId, // 使用task_id作为临时ID
        message_type: "reply_message",
        sender_email: replyToEmail,
        sender_name: targetAccount?.email.split('@')[0] || "EmailCare客服",
        recipient_email: userEmail,
        subject: subject,
        content: isRichText ? replyHtmlContent : replyContent,
        html_content: isRichText ? replyHtmlContent : undefined,
        sent_time: new Date().toISOString(),
        ai_analysis_status: "none",
        sending_status: "sending",
        is_read: true,
        is_important: false
      };
      
      // 立即添加到消息列表
      setMessages(prevMessages => [...prevMessages, tempReplyMessage]);
      setShouldAutoScroll(true); // 发送回复后自动滚动到底部
      
      // 清空编辑器
      setReplyContent('');
      setReplyHtmlContent('');
      setReplySubject('');
      setIsComposerExpanded(false);
      
      // 设置超时机制，如果30秒内没有收到SSE更新，自动标记为已发送
      setTimeout(() => {
        setMessages(prevMessages => 
          prevMessages.map(msg => {
            if (msg.id === taskId && msg.sending_status === 'sending') {
              console.log('⏰ 超时自动更新消息状态为已发送:', taskId);
              return {
                ...msg,
                sending_status: 'sent'
              };
            }
            return msg;
          })
        );
      }, 30000); // 30秒超时
      
      // SSE会自动更新发送状态，不需要手动轮询
      
    } catch (error) {
      console.error('发送回复失败:', error);
      
      // 检查是否是重复发送错误
      if (error instanceof Error && error.message.includes('429')) {
        window.alert('检测到重复发送，请稍后再试');
      } else if (error instanceof Error && error.message.includes('检测到重复发送')) {
        window.alert('检测到重复发送，请稍后再试');
      } else {
        window.alert('发送回复失败，请重试');
      }
    } finally {
      setSendingReply(false);
    }
  };

  const toggleMessageExpansion = (messageId: number) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const toggleAIAnalysisExpansion = (messageId: number) => {
    setUnexpandedAIAnalysis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const toggleOriginalContent = (messageId: number) => {
    setShowingOriginal(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // 处理回复建议内容编辑
  const handleSuggestedReplyChange = (messageId: number, newContent: string) => {
    setEditingSuggestedReplies(prev => ({
      ...prev,
      [messageId]: newContent
    }));
  };

  // 获取回复建议的当前编辑内容
  const getSuggestedReplyContent = (message: ChatMessage) => {
    return editingSuggestedReplies[message.id] || message.ai_analysis?.suggested_reply || '';
  };

  // 立即回复功能
  const handleDirectReply = async (message: ChatMessage) => {
    const replyContent = getSuggestedReplyContent(message);
    if (!replyContent.trim() || !userEmail) return;

    // 防止重复提交
    if (sendingDirectReply.has(message.id)) {
      console.log('⚠️ 正在发送中，忽略重复点击');
      return;
    }

    // 添加到发送状态
    setSendingDirectReply(prev => new Set(prev).add(message.id));

    try {
      // 构造回复主题
      const subject = message.subject.startsWith('Re:') ? message.subject : `Re: ${message.subject}`;
      
      // 查找用户邮件（message_type为user_message）来确定原始收件人
      const userMessage = messages.find(msg => msg.message_type === 'user_message');
      const replyToEmail = userMessage?.recipient_email || 'service@digen.ai';
      
      // 获取所有邮箱账号来查找对应的account_id
      const accountsResponse = await apiService.get('/accounts');
      const accounts = accountsResponse.data.accounts || [];
      const targetAccount = accounts.find((acc: any) => acc.email === replyToEmail);
      const accountId = targetAccount?.id || 6;
      
      // 发送邮件
      const response = await apiService.post('/emails/send', {
        from: replyToEmail,
        to: userEmail,
        subject: subject,
        content: replyContent,
        account_id: accountId
      });
      
      // 获取任务ID作为临时消息ID
      const taskId = response.data.task_id;
      
      // 立即创建一个临时的回复消息显示在页面上
      const tempReplyMessage: ChatMessage = {
        id: taskId,
        message_type: "reply_message",
        sender_email: replyToEmail,
        sender_name: targetAccount?.email.split('@')[0] || "EmailCare客服",
        recipient_email: userEmail,
        subject: subject,
        content: replyContent,
        sent_time: new Date().toISOString(),
        ai_analysis_status: "none",
        sending_status: "sending",
        is_read: true,
        is_important: false
      };
      
      // 立即添加到消息列表
      setMessages(prevMessages => [...prevMessages, tempReplyMessage]);
      setShouldAutoScroll(true); // 直接回复后自动滚动到底部
      
      // 清除编辑状态
      setEditingSuggestedReplies(prev => {
        const newState = { ...prev };
        delete newState[message.id];
        return newState;
      });
      
      // 设置超时机制，如果30秒内没有收到SSE更新，自动标记为已发送
      setTimeout(() => {
        setMessages(prevMessages => 
          prevMessages.map(msg => {
            if (msg.id === taskId && msg.sending_status === 'sending') {
              console.log('⏰ 超时自动更新消息状态为已发送:', taskId);
              return {
                ...msg,
                sending_status: 'sent'
              };
            }
            return msg;
          })
        );
      }, 30000); // 30秒超时
      
      console.log('立即回复发送成功，任务ID:', taskId);
      
    } catch (error) {
      console.error('立即回复发送失败:', error);
      
      // 检查是否是重复发送错误
      if (error instanceof Error && error.message.includes('429')) {
        window.alert('检测到重复发送，请稍后再试');
      } else if (error instanceof Error && error.message.includes('检测到重复发送')) {
        window.alert('检测到重复发送，请稍后再试');
      } else {
        window.alert('发送回复失败，请重试');
      }
    } finally {
      // 移除发送状态
      setSendingDirectReply(prev => {
        const newSet = new Set(prev);
        newSet.delete(message.id);
        return newSet;
      });
    }
  };

  // 获取最后一份用户邮件的AI分析建议回复
  const getLastUserEmailSuggestedReply = () => {
    // 从后往前找最后一份用户邮件
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.message_type === 'user_message' && 
          message.ai_analysis_status === 'completed' && 
          message.ai_analysis?.suggested_reply) {
        return {
          message_id: message.id,
          suggestedReply: message.ai_analysis.suggested_reply,
          subject: message.subject
        };
      }
    }
    return null;
  };

  // 获取中文翻译内容
  const getChineseTranslationContent = (message: ChatMessage) => {
    const key = `chinese_${message.id}`;
    return editingSuggestedReplies[key] || message.ai_analysis?.reply_chinese_translation || '';
  };

  // 处理中文翻译内容变化
  const handleChineseTranslationChange = (messageId: number, newContent: string) => {
    const key = `chinese_${messageId}`;
    setEditingSuggestedReplies(prev => ({
      ...prev,
      [key]: newContent
    }));
  };

  // 将中文翻译转换为英文回复建议
  const handleTranslateChinese = async (message: ChatMessage) => {
    const chineseContent = getChineseTranslationContent(message);
    if (!chineseContent.trim()) {
      window.alert('请先输入中文翻译内容');
      return;
    }

    setTranslating(prev => new Set(prev).add(message.id));

    try {
      const response = await fetch(`${getApiBaseUrl()}/translation/translate-chinese-to-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chinese_content: chineseContent,
          target_language: "en"
        }),
      });

      if (!response.ok) {
        throw new Error(`翻译失败: ${response.status}`);
      }

      const result = await response.json();
      
      // 更新回复建议内容
      setEditingSuggestedReplies(prev => ({
        ...prev,
        [message.id]: result.suggested_reply || result.english_reply || ''
      }));
    } catch (error) {
      console.error('翻译失败:', error);
      window.alert(`翻译失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setTranslating(prev => {
        const newSet = new Set(prev);
        newSet.delete(message.id);
        return newSet;
      });
    }
  };

  const renderAttachments = (message: ChatMessage) => {
    if (!message.has_attachments || !message.attachments || message.attachments.length === 0) {
      return null;
    }

    // 过滤掉无效的附件
    const validAttachments = message.attachments.filter(att => att && att.filename);
    
    if (validAttachments.length === 0) {
      return null;
    }

    const totalSize = validAttachments.reduce((sum, att) => sum + (att.size || 0), 0);
    
    return (
      <div className="mt-3 border-t pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Paperclip size={14} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              附件 ({validAttachments.length})
            </span>
          </div>
          {totalSize > 0 && (
            <span className="text-xs text-gray-500">
              总大小: {formatFileSize(totalSize)}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <div className="flex gap-3 pb-2">
            {validAttachments.map((attachment, index) => {
              const FileIcon = getFileIcon(attachment.content_type);
              const isPreview = isPreviewable(attachment.content_type);
              const isImage = attachment.content_type.startsWith('image/');
              const iconColor = getFileIconColor(attachment.content_type);
              const attachmentKey = `${attachment.filename}_${attachment.size}`;
              const isLoading = loadingAttachments.has(attachmentKey);
              
              return (
                <div
                  key={index}
                  className="flex-shrink-0 w-20 h-20 relative group cursor-pointer"
                  onClick={() => handleAttachmentClick(attachment, validAttachments)}
                >
                  {/* 缩略图/图标背景 */}
                  <div className="w-full h-full rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200 group-hover:border-gray-300 transition-colors">
                    {isImage && attachment.s3_url ? (
                      <img
                        src={attachment.s3_url}
                        alt={attachment.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // 如果图片加载失败，显示文件图标
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${isImage && attachment.s3_url ? 'hidden' : ''}`}>
                      <FileIcon 
                        size={20} 
                        className={iconColor} 
                      />
                    </div>
                  </div>
                  
                  {/* 悬浮信息层 */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 rounded-lg transition-all duration-200 flex flex-col justify-between p-2">
                    {/* 文件名 - 顶部 */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="text-xs font-medium text-white truncate" title={attachment.filename}>
                        {attachment.filename}
                      </div>
                    </div>
                    
                    {/* 文件大小和操作按钮 - 底部 */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center gap-1">
                      <div className="flex justify-center">
                        {isLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : isPreview ? (
                          <span className="text-xs text-white bg-blue-600 px-2 py-1 rounded">
                            预览
                          </span>
                        ) : (
                          <div className="bg-white bg-opacity-80 rounded-full p-1">
                            <Download size={14} className="text-gray-700" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderAIAnalysis = (message: ChatMessage) => {
    const isAnalyzing = analyzingMessages.has(message.id) || message.ai_analysis_status === 'analyzing';
    const isUnexpanded = unexpandedAIAnalysis.has(message.id);
    const isExpanded = !isUnexpanded;
    
    if (isAnalyzing) {
      return (
        <div className="mt-2 p-2 theme-blue-bg border theme-blue-border rounded-md">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-xs theme-blue-text font-medium">AI分析中...</span>
          </div>
        </div>
      );
    }

    if (message.ai_analysis_status === 'none') {
      return (
        <div className="mt-2">
          <button
            onClick={() => handleAnalyzeMessage(message.id)}
            className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition"
          >
            <RefreshCw size={12} />
            AI分析
          </button>
        </div>
      );
    }

    if (message.ai_analysis_status === 'pending') {
      return (
        <div className="mt-2 p-2 theme-yellow-bg border theme-yellow-border rounded-md">
          <div className="flex items-center">
            <div className="animate-pulse h-3 w-3 bg-yellow-500 rounded-full mr-2"></div>
            <span className="text-xs theme-yellow-text font-medium">AI分析排队中...</span>
          </div>
        </div>
      );
    }

    if (message.ai_analysis_status === 'failed') {
      return (
        <div className="mt-2 theme-red-bg border theme-red-border rounded-md">
          <div className="flex items-center justify-between p-2">
                          <div 
                className="flex items-center cursor-pointer hover:bg-red-100 transition-colors flex-1"
                onClick={() => toggleAIAnalysisExpansion(message.id)}
              >
                <AlertCircle className="h-3 w-3 text-red-500 mr-2" />
                <span className="text-xs theme-red-text font-medium">AI分析失败</span>
                {isExpanded ? <ChevronUp size={14} className="theme-red-text ml-2" /> : <ChevronDown size={14} className="theme-red-text ml-2" />}
              </div>
            <button
              onClick={() => handleAnalyzeMessage(message.id)}
              className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors ml-2"
            >
              <RefreshCw size={10} />
              重试
            </button>
          </div>
          
          {isExpanded && message.ai_analysis && (
            <div className="px-2 pb-2 space-y-2 text-xs border-t border-red-200 pt-2">
              <div className="text-red-700 font-medium">失败详情:</div>
              
              {/* 显示失败的分析内容 */}
              {message.ai_analysis.chinese_translation && (
                <div>
                  <span className="font-medium text-red-600 block mb-1">翻译结果:</span>
                  <div className="bg-red-100 p-2 rounded border border-red-200 text-red-800 text-xs copyable">
                    {message.ai_analysis.chinese_translation}
                  </div>
                </div>
              )}
              
              {message.ai_analysis.chinese_summary && (
                <div>
                  <span className="font-medium text-red-600 block mb-1">摘要结果:</span>
                  <div className="bg-red-100 p-2 rounded border border-red-200 text-red-800 text-xs">
                    {message.ai_analysis.chinese_summary}
                  </div>
                </div>
              )}
              
              {message.ai_analysis.tags && message.ai_analysis.tags.length > 0 && (
                <div>
                  <span className="font-medium text-red-600 block mb-1">标签:</span>
                  <div className="flex flex-wrap gap-1">
                    {message.ai_analysis.tags.map((tag, index) => (
                      <span key={index} className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {message.ai_analysis.category && (
                <div>
                  <span className="font-medium text-red-600 block mb-1">分类:</span>
                  <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                    {message.ai_analysis.category}
                  </span>
                </div>
              )}
              
              <div className="text-xs text-red-600 bg-red-100 p-2 rounded">
                💡 提示：AI分析过程中遇到问题，请点击"重试"按钮重新分析，或联系技术支持。
              </div>
            </div>
          )}
        </div>
      );
    }

    if (message.ai_analysis_status === 'completed' && message.ai_analysis) {
      return (
        <div className="mt-2 theme-blue-bg border theme-blue-border rounded-md">
          <div className="flex items-center justify-between p-2">
            <div 
              className="flex items-center cursor-pointer hover:bg-blue-100 transition-colors flex-1 min-w-0"
              onClick={() => toggleAIAnalysisExpansion(message.id)}
            >
              <Check className="h-3 w-3 theme-blue-text mr-2 flex-shrink-0" />
              <div className="flex items-center flex-wrap gap-1 min-w-0 flex-1">
                <span className="text-xs theme-blue-text font-medium">AI分析完成</span>
                {/* 直接显示标签 */}
                {message.ai_analysis.tags && message.ai_analysis.tags.length > 0 && (
                  <>
                    <span className="text-xs theme-blue-text">·</span>
                    <div className="flex flex-wrap gap-1">
                      {message.ai_analysis.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="px-1.5 py-0.5 bg-blue-100 theme-blue-text rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                      {message.ai_analysis.tags.length > 3 && (
                        <span className="text-xs theme-blue-text">+{message.ai_analysis.tags.length - 3}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
              {isExpanded ? <ChevronUp size={14} className="theme-blue-text ml-1 flex-shrink-0" /> : <ChevronDown size={14} className="theme-blue-text ml-1 flex-shrink-0" />}
            </div>
            <button
              onClick={() => handleAnalyzeMessage(message.id)}
              className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition ml-2 flex-shrink-0"
              disabled={analyzingMessages.has(message.id)}
            >
              <RefreshCw size={10} className={analyzingMessages.has(message.id) ? 'animate-spin' : ''} />
              重新分析
            </button>
          </div>
          
          {isExpanded && (
            <div className="px-2 pb-2 space-y-2 text-xs border-t theme-blue-border">
              {/* 中文翻译 */}
              {message.ai_analysis.chinese_translation && (
                <div className="gap-2 pt-2">
                  <span className="font-medium theme-text-secondary block mb-1 text-xs">中文翻译:</span>
                  <div className="theme-bg-primary p-2 rounded border theme-border-primary theme-text-primary text-xs">
                    {message.ai_analysis.chinese_translation}
                  </div>
                </div>
              )}

              {/* 回复中文翻译 */}
              {message.ai_analysis.reply_chinese_translation && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium theme-text-secondary text-xs">回复中文翻译:</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleTranslateChinese(message)}
                        disabled={translating.has(message.id)}
                        className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title="将中文翻译转换为英文回复建议"
                      >
                        {translating.has(message.id) ? (
                          <>
                            <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white"></div>
                            翻译中...
                          </>
                        ) : (
                          <>
                            <Languages size={10} />
                            翻译
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <textarea
                      value={getChineseTranslationContent(message)}
                      onChange={(e) => handleChineseTranslationChange(message.id, e.target.value)}
                      className="w-full flex-1 theme-bg-primary p-2 rounded border theme-border-primary theme-text-primary text-xs"
                      placeholder="请输入中文翻译..."
                      style={{ minHeight: '150px' }}
                    />
                </div>
              )}

              {/* 回复建议 */}
              {message.ai_analysis.suggested_reply && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium theme-text-secondary text-xs">回复建议:</span>
                    <button
                      onClick={() => handleDirectReply(message)}
                      disabled={sendingDirectReply.has(message.id)}
                      className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingDirectReply.has(message.id) ? (
                        <>
                          <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white"></div>
                          发送中...
                        </>
                      ) : (
                        <>
                          <Send size={10} />
                          立即回复
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={getSuggestedReplyContent(message)}
                    onChange={(e) => handleSuggestedReplyChange(message.id, e.target.value)}
                    className="w-full flex-1 theme-bg-primary p-2 rounded border theme-border-primary theme-text-primary text-xs"
                    placeholder="请输入邮件内容..."
                    style={{ minHeight: '150px' }}
                  />
                </div>
              )}

              {/* 完整标签列表 */}
              {message.ai_analysis.tags && message.ai_analysis.tags.length > 0 && (
                <div>
                  <span className="font-medium theme-text-secondary block mb-1 text-xs">标签:</span>
                  <div className="flex flex-wrap gap-1">
                    {message.ai_analysis.tags.map((tag, index) => (
                      <span key={index} className="px-1.5 py-0.5 theme-bg-secondary theme-text-secondary rounded text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderMessageContent = (message: ChatMessage) => {
    const isExpanded = expandedMessages.has(message.id);
    const displayContent = getDisplayContent(message);
    const hasHtmlContent = message.html_content && message.html_content.trim();
    const shouldUseHtml = hasHtmlContent || isHtmlContent(displayContent);
    // 用实际高度判断是否需要折叠
    const needsCollapse = needsCollapseMap[message.id];
    const maxHeight = isExpanded ? 'none' : '200px';
    return (
      <div className="relative">
        <div
          ref={el => { messageContentRefs.current[message.id] = el; }}
          className={`text-sm overflow-hidden transition-all duration-200 ${
            !isExpanded && needsCollapse ? 'email-content-collapsed' : ''
          }`}
          style={{ maxHeight }}
        >
          {shouldUseHtml ? (
            <div
              className="email-content"
              dangerouslySetInnerHTML={{
                __html: hasHtmlContent ? message.html_content! : displayContent
              }}
            />
          ) : (
            <div className="email-content whitespace-pre-wrap">{displayContent}</div>
          )}
        </div>
        {needsCollapse && (
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => toggleMessageExpansion(message.id)}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-900 dark:text-gray-900 rounded transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={12} />
                  收起
                </>
              ) : (
                <>
                  <ChevronDown size={12} />
                  展开
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMessageBubble = (message: ChatMessage) => {
    const isUserMessage = message.message_type === 'user_message';
    
    return (
      <div
        key={message.id}
        className={`flex mb-6 ${isUserMessage ? 'justify-start' : 'justify-end'}`}
      >
        <div
          className={`w-full max-w-[85%] px-4 py-3 rounded-lg theme-shadow-sm theme-bg-primary border-2 ${
            isUserMessage
              ? 'theme-blue-border theme-text-primary'
              : 'theme-green-border theme-text-primary'
          }`}
        >
          <div className={`flex items-center justify-between mb-2 text-xs ${
            isUserMessage ? 'theme-blue-text' : 'theme-green-text'
          }`}>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {isUserMessage 
                  ? (message.sender_email)
                  : (message.sender_email) // 显示邮箱账号名称
                }
              </span>
              {/* 附件指示器 */}
              {message.has_attachments && message.attachment_count && message.attachment_count > 0 && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 theme-bg-secondary rounded-full">
                  <Paperclip size={10} className="theme-text-secondary" />
                  <span className="text-xs theme-text-secondary">{message.attachment_count}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span>{formatTime(message.sent_time || '')}</span>
              {/* 显示发送状态 */}
              {!isUserMessage && message.sending_status && (
                <div className="flex items-center gap-1">
                  {message.sending_status === 'sending' && (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500"></div>
                      <span className="text-orange-600 text-xs">发送中</span>
                    </>
                  )}
                  {message.sending_status === 'sent' && (
                    <>
                      <Check size={12} className="theme-green-text" />
                      <span className="theme-green-text text-xs">已发送</span>
                    </>
                  )}
                  {message.sending_status === 'failed' && (
                    <>
                      <AlertCircle size={12} className="theme-red-text" />
                      <span className="theme-red-text text-xs">发送失败</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {message.subject && !message.subject.startsWith('Re:') && (
            <div className="text-sm font-medium mb-3 theme-text-primary">
              {message.subject}
            </div>
          )}
          
          {renderMessageContent(message)}
          
          {renderAttachments(message)}
          
          {isUserMessage && renderAIAnalysis(message)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full theme-bg-secondary">
      {chatRoom && (
        <div className="border-b theme-border-primary theme-bg-primary px-4 py-3 theme-shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div>
                <h2 className="text-sm font-semibold theme-text-primary">
                  {chatRoom.user_name || (chatRoom.user_email ? chatRoom.user_email.split('@')[0] : '未知用户')}
                </h2>
                <p className="text-xs theme-text-secondary">{chatRoom.user_email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* 用户ID模块 */}
              <div className="flex items-center gap-2">
                {!isEditingUserId ? (
                  <>
                    {chatRoom.user_id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs theme-text-secondary">用户ID:</span>
                        <span
                          className="inline-flex items-center px-2 py-1 text-xs theme-blue-bg theme-blue-text rounded cursor-pointer hover:bg-blue-200 theme-transition"
                          onClick={handleStartEditUserId}
                          title={chatRoom.user_id}
                        >
                          <User className="w-3 h-3 mr-1" />
                          {chatRoom.user_id}
                        </span>
                        <button
                          onClick={handleStartEditUserId}
                          className="theme-text-tertiary hover:text-gray-600 theme-transition"
                          title="编辑用户ID"
                        >
                          <Edit size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleStartEditUserId}
                        className="inline-flex items-center px-2 py-1 text-xs theme-text-secondary border border-dashed theme-border-secondary rounded hover:border-gray-400 hover:text-gray-600 theme-transition"
                      >
                        <Plus className="w-2 h-2 mr-1" />
                        添加用户ID
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs theme-text-secondary">用户ID:</span>
                    <input
                      type="text"
                      value={editingUserId}
                      onChange={(e) => setEditingUserId(e.target.value)}
                      placeholder="输入用户ID"
                      className="px-2 py-1 text-sm theme-input theme-border-focus"
                      style={{ width: '150px' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveUserId();
                        } else if (e.key === 'Escape') {
                          handleCancelEditUserId();
                        }
                      }}
                      autoFocus
                    />
                    <button
                      onClick={handleSaveUserId}
                      disabled={updatingUserId}
                      className="text-green-600 hover:text-green-700 disabled:opacity-50"
                      title="保存"
                    >
                      {updatingUserId ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      ) : (
                        <CheckCircle size={16} />
                      )}
                    </button>
                    <button
                      onClick={handleCancelEditUserId}
                      disabled={updatingUserId}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="取消"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* 标签模块 */}
              <div className="flex items-center gap-2">
                {!isEditingTags ? (
                  <>
                    {chatRoom.tags && chatRoom.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {chatRoom.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium theme-blue-bg theme-blue-text"
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm theme-text-tertiary">无标签</span>
                    )}
                    <button
                      onClick={handleStartEditTags}
                      className="theme-text-tertiary hover:text-gray-600 theme-transition"
                      title="编辑标签"
                    >
                      <Edit size={16} />
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editingTags}
                      onChange={(e) => setEditingTags(e.target.value)}
                      placeholder="输入标签，用逗号分隔"
                      className="px-2 py-1 text-sm theme-input theme-border-focus"
                      style={{ width: '200px' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveTags();
                        } else if (e.key === 'Escape') {
                          handleCancelEditTags();
                        }
                      }}
                      autoFocus
                    />
                    <button
                      onClick={handleSaveTags}
                      disabled={updatingTags}
                      className="text-green-600 hover:text-green-700 disabled:opacity-50"
                      title="保存"
                    >
                      {updatingTags ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      ) : (
                        <Check size={16} />
                      )}
                    </button>
                    <button
                      onClick={handleCancelEditTags}
                      disabled={updatingTags}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="取消"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="text-center theme-text-secondary mt-8">
            暂无消息
          </div>
        ) : (
          messages.map(renderMessageBubble)
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t theme-border-primary theme-bg-primary">
        {!isComposerExpanded ? (
          <div className="relative">
            <button
              onClick={() => {
                setIsComposerExpanded(true);
                
                // 延迟滚动，等待编辑器渲染完成
                setTimeout(() => {
                  setShouldAutoScroll(true);
                }, 100);
                
                // 自动填充最后一份用户邮件的建议回复
                const lastSuggestedReply = getLastUserEmailSuggestedReply();
                if (lastSuggestedReply) {
                  const replyText = lastSuggestedReply.suggestedReply;
                  setReplyContent(replyText);
                  setReplyHtmlContent(replyText.replace(/\n/g, '<br>'));
                  
                  // 设置回复主题
                  const subject = lastSuggestedReply.subject.startsWith('Re:') ? 
                    lastSuggestedReply.subject : 
                    `Re: ${lastSuggestedReply.subject}`;
                  setReplySubject(subject);
                }
              }}
              className="fixed bottom-6 right-6 w-14 h-14 theme-blue-button text-white rounded-full theme-shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 group"
              title="回复邮件"
            >
              <Send size={20} />
              <span className="absolute right-16 theme-bg-tertiary theme-text-primary px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                回复邮件
              </span>
            </button>
          </div>
        ) : (
          <div className="border-t">
            <div className="flex items-center justify-between p-3 theme-bg-secondary theme-border-primary border-b">
              <h3 className="font-medium theme-text-primary">回复邮件</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsRichText(!isRichText)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border theme-transition ${
                    isRichText 
                      ? 'theme-blue-bg theme-blue-border theme-blue-text'
                      : 'theme-bg-secondary theme-border-secondary theme-text-secondary'
                  }`}
                >
                  {isRichText ? <Type className="w-3 h-3" /> : <Code className="w-3 h-3" />}
                  <span>{isRichText ? '富文本' : '纯文本'}</span>
                </button>
                <button
                  onClick={() => setIsComposerExpanded(false)}
                  className="theme-text-secondary hover:text-gray-700 theme-transition"
                >
                  <ChevronDown size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  主题
                </label>
                <input
                  type="text"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  placeholder={messages.length > 0 ? 
                    (messages[messages.length - 1].subject.startsWith('Re:') ? 
                      messages[messages.length - 1].subject : 
                      `Re: ${messages[messages.length - 1].subject}`
                    ) : 'Re: 回复'
                  }
                  className="w-full px-3 py-2 theme-input theme-border-focus rounded-md"
                />
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  内容
                </label>
                {isRichText ? (
                  <div className="theme-border-primary border rounded-md overflow-hidden" style={{ height: '300px' }}>
                    <ReactQuill
                      value={replyHtmlContent}
                      onChange={setReplyHtmlContent}
                      modules={quillModules}
                      formats={[
                        'header', 'bold', 'italic', 'underline',
                        'color', 'background', 'list', 'bullet',
                        'link'
                      ]}
                      style={{ height: '260px' }}
                      placeholder="输入回复内容..."
                    />
                  </div>
                ) : (
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="输入回复内容..."
                    rows={12}
                    className="w-full px-3 py-2 theme-input theme-border-focus rounded-md resize-none"
                    style={{ height: '300px' }}
                  />
                )}
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsComposerExpanded(false)}
                  className="px-4 py-2 theme-text-secondary hover:text-gray-800 theme-transition"
                  disabled={sendingReply}
                >
                  取消
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={(!replyContent.trim() && !replyHtmlContent.trim()) || sendingReply}
                  className="px-4 py-2 theme-blue-button text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sendingReply ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      发送中...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      发送回复
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 添加附件预览组件 */}
      <AttachmentPreview
        attachments={previewAttachments}
        currentIndex={previewCurrentIndex}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onIndexChange={setPreviewCurrentIndex}
      />
    </div>
  );
};

export default ChatRoomView; 